import { useParams } from "react-router-dom";
import moment from "moment";

import { useGetQuotations } from "api/queries/quotes/useGetQuotations";
import usePermissions from "hooks/usePermissions";
import { IQuotation } from "pages/shipper/ShipmentQuotes/components/quotes/QuotesType";
import QuotationFooterMainActions from "./QuotationFooterMainActions";
import QuotationFooterSecondaryActions from "./QuotationFooterSecondaryActions";
import QuotationFooterApprovalRequestActions from "./QuotationFooterApprovalRequestActions";
import QuotationFooterRequestExtension from "./QuotationFooterRequestExtension";
import useApprovalFlowDialogs from "../hooks/useApprovalFlowDialogs";

interface QuotationFooterActionsProps {
  quotation: any;
}

export default function QuotationFooterActions({ quotation }: QuotationFooterActionsProps) {
  const { id } = useParams();

  const { permissions } = usePermissions();
  const parsedPermissions = JSON.parse(permissions || "{}");
  const { booking } = parsedPermissions?.permissions || {};
  const { menu_items } = parsedPermissions || {};

  const { actions, status, general, created_on_newui, created_by_user_type } = quotation ?? {};

  const { data: quotationsData } = useGetQuotations({
    shipmentId: id!,
    options: {
      enabled: !!id,
    },
  });

  const isQuotationExpired = general.quote_expiration_seconds_remaining < 0;

  const quotationSettings = quotationsData?.settings;
  const hasOneQuotePendingApproval = quotationsData?.quotations?.some(
    (quote: IQuotation) => quote.status === "require_approval",
  );

  // An in-transit (post booking) cost re-approval request leaves the quote status at
  // "reapproval_needed_after_booking", so the pending request has to be detected from
  // can_cancel_approval_request, which is only true while a request the user raised is open.
  const hasPendingApprovalRequest =
    status === "require_approval" ||
    (status === "reapproval_needed_after_booking" && actions.can_cancel_approval_request);

  const quotationRequestedApproval =
    !actions.can_approve &&
    !actions.can_preapprove &&
    hasPendingApprovalRequest &&
    (!actions.can_book || (actions.can_book && actions.can_cancel_approval_request));

  const showActiveActionButtons =
    status === "reapproval_needed_after_booking" ||
    moment(general.quote_expiration, "YYYY-MM-DD HH:mm:ss ZZ").isAfter(moment()) ||
    hasOneQuotePendingApproval;

  // =============== Main CTA buttons ===============
  const showManageApprovalButton =
    (!isQuotationExpired && (actions.can_approve || actions.can_preapprove) && status === "require_approval") ||
    (actions.can_book && status === "reapproval_needed_after_booking");

  const showBookButton =
    !isQuotationExpired &&
    actions.can_book &&
    booking?.can_request_book_shipments &&
    !actions.can_approve &&
    !showManageApprovalButton &&
    status !== "reapproval_needed_after_booking";

  // Based on V1 condition
  const disableRequestApprovalButton =
    !actions.can_select ||
    hasOneQuotePendingApproval ||
    (!actions.reapproval_needed_after_booking && status === "reapproval_needed_after_booking");
  const showRequestApprovalButton =
    !disableRequestApprovalButton &&
    !isQuotationExpired &&
    booking?.can_request_book_shipments &&
    !actions.can_book &&
    (status === "open_to_book" ||
      status === "submitted" ||
      (status === "reapproval_needed_after_booking" && actions.reapproval_needed_after_booking));

  const showRequestExtensionButton =
    isQuotationExpired &&
    menu_items?.create_quote_requests &&
    booking?.can_request_book_shipments &&
    !["reapproval_needed_after_booking", "canceled", "Auto canceled", "canceled_after_booking"].includes(status);
  const hasAlreadyRequestedExtension = general.extension_requested_at && general.extension_requested_by;
  // =============== END ===============

  // =============== Secondary CTA buttons ===============
  const showEditAndCancelButtons =
    created_on_newui && created_by_user_type === "shipper" && status !== "canceled" && status !== "Auto canceled";

  const showSuggestApprovalButton =
    showBookButton && quotationSettings?.can_suggest_approve && actions?.can_suggest_approval && !hasOneQuotePendingApproval;
  // =============== END ===============

  // =============== Manage approval request dropdown ===============
  const showAddAdditionalApprovers =
    actions.can_add_additional_approvers && !actions.can_approve && hasPendingApprovalRequest;
  const showCancelApprovalRequest = quotationRequestedApproval && actions.can_cancel_approval_request;
  // =============== END ===============

  const {
    handleClickRequestApproval,
    handleClickManageApproval,
    handleClickBook,
    handleClickEditManualQuote,
    handleClickCancelManualQuote,
    renderApprovalFlowDialogs,
  } = useApprovalFlowDialogs({
    quotation,
    showManageApprovalButton,
    showActiveActionButtons,
  });

  return (
    <>
      {renderApprovalFlowDialogs()}
      <QuotationFooterSecondaryActions
        showEditAndCancelButtons={showEditAndCancelButtons}
        showSuggestApprovalButton={showSuggestApprovalButton}
        handleClickRequestApproval={handleClickRequestApproval}
        handleClickEditManualQuote={handleClickEditManualQuote}
        handleClickCancelManualQuote={handleClickCancelManualQuote}
      />

      {showActiveActionButtons && (
        <QuotationFooterMainActions
          showManageApprovalButton={showManageApprovalButton}
          showBookButton={showBookButton}
          showRequestApprovalButton={showRequestApprovalButton}
          handleClickRequestApproval={handleClickRequestApproval}
          handleClickManageApproval={handleClickManageApproval}
          handleClickBook={handleClickBook}
        />
      )}

      {showRequestExtensionButton && (
        <QuotationFooterRequestExtension hasAlreadyRequestedExtension={hasAlreadyRequestedExtension} quotation={quotation} />
      )}

      <QuotationFooterApprovalRequestActions
        showAddAdditionalApprovers={showAddAdditionalApprovers}
        showCancelApprovalRequest={showCancelApprovalRequest}
        quotation={quotation}
        handleClickRequestApproval={handleClickRequestApproval}
      />
    </>
  );
}
